import type { TrackEventOptions, TrackPageOptions } from '@cego/tracking'
import { createSharedComposable } from '@vueuse/core'
import { onNuxtReady } from '#app'
import { useCoreConfig, useVisitorIdCookie } from '#imports'

type QueuedCall
    = | { type: 'event', payload: TrackEventOptions }
        | { type: 'page', payload: TrackPageOptions }

export const useTracking = createSharedComposable(() => {
    if (import.meta.server) {
        return {
            trackEvent: async (_payload: TrackEventOptions) => {},
            trackPageRequest: async (_payload: TrackPageOptions) => {},
        }
    }

    const { trackingService, site } = useCoreConfig()
    const { setVisitorIdCookie } = useVisitorIdCookie()

    const queue: QueuedCall[] = []
    let TrackingManual: typeof import('@cego/tracking').TrackingManual | null = null

    async function processEvent(payload: TrackEventOptions) {
        if (!trackingService?.enabled) return
        setVisitorIdCookie()
        const { event_type = '', event_name = '', meta_data = [], log_entry = false } = payload
        await TrackingManual!.trackEvent({
            event_name,
            event_type,
            meta_data,
            ...(log_entry && { log_entry }),
        })
    }

    async function processPage(payload: TrackPageOptions) {
        if (!trackingService?.enabled) return
        setVisitorIdCookie()
        const { url = '', referer = '' } = payload
        await TrackingManual!.trackPage({
            url,
            referer,
        })
    }

    async function flush() {
        while (queue.length > 0) {
            const item = queue.shift()!
            if (item.type === 'event') {
                await processEvent(item.payload)
            }
            else {
                await processPage(item.payload)
            }
        }
    }

    onNuxtReady(async () => {
        const mod = await import('@cego/tracking')
        TrackingManual = mod.TrackingManual
        new TrackingManual({ brand: site.brand })
        await flush()
    })

    return {
        trackEvent: async (payload: TrackEventOptions) => {
            if (!TrackingManual) {
                queue.push({ type: 'event', payload })
                return
            }
            await processEvent(payload)
        },
        trackPageRequest: async (payload: TrackPageOptions) => {
            if (!TrackingManual) {
                queue.push({ type: 'page', payload })
                return
            }
            await processPage(payload)
        },
    }
})
